import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useMemo, useEffect, useCallback } from "react";
import { ArrowLeft, Search, CirclePlus, Loader2, X } from "lucide-react";
import toast from "react-hot-toast";
import AlmacenFormModal, { type AlmacenFormData } from '@/features/almacen/components/AlmacenFormModal';
import AlmacenDetailView, { type Almacen } from '@/features/almacen/components/AlmacenDetailView';
import { almacenApi, type WarehouseApiResponse } from '@/features/almacen/api/almacen.api';
import { apiService } from '@/services/core/api.service';
import type { UserProfile } from '@/services/api';
import SelectBuscable from '@/shared/components/SelectBuscable';
import { Badge } from '@/shared/components/Badge';
import FilterSelect from '@/shared/components/FilterSelect';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

// ─── Error → mensaje amigable ─────────────────────────────────────────────────

function friendlyError(err: unknown): string {
  const e = err as { statusCode?: number; message?: string | string[] };
  if (e.statusCode === 500) return "Error del servidor. Inténtalo de nuevo o contacta al administrador.";
  const msgs: string[] = Array.isArray(e.message) ? e.message : [e.message ?? "Error desconocido"];
  return msgs.join(" ");
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildUsersMap(users: UserProfile[]): Record<string, string> {
  const map: Record<string, string> = {};
  users.forEach((u) => { map[u.id] = `${u.firstName} ${u.lastName}`.trim(); });
  return map;
}

function mapWarehouse(w: WarehouseApiResponse, usersMap: Record<string, string>): Almacen {
  return {
    id: w.id,
    nombre: w.name ?? "",
    responsable: w.responsibleUserId
      ? (usersMap[w.responsibleUserId] ?? w.responsibleUserId)
      : "Sin responsable",
    descripcion: w.description ?? "",
    herramientas: 0, // backend doesn't expose this count yet
    proyecto: "Sin proyecto",   // backend doesn't have this field yet
    estado: w.isActive ? "Activo" : "Inactivo",
  };
}

// ─── Dropdown ─────────────────────────────────────────────────────────────────

// ─── Reasignar Herramientas Modal ─────────────────────────────────────────────

interface ToolSummary {
  id: string;
  name: string;
  serialCode: string;
}

interface ReasignModalProps {
  warehouseName: string;
  toolCount: number;
  otherWarehouses: WarehouseApiResponse[];
  onConfirm: (targetWarehouseId: string) => Promise<void>;
  onCancel: () => void;
}

const ReasignHerramientasModal = ({
  warehouseName,
  toolCount,
  otherWarehouses,
  onConfirm,
  onCancel,
}: ReasignModalProps) => {
  const [targetId, setTargetId] = useState("");
  const [saving, setSaving]     = useState(false);

  const handleConfirm = async () => {
    if (!targetId || saving) return;
    setSaving(true);
    try {
      await onConfirm(targetId);
    } catch (err: unknown) {
      toast.error(friendlyError(err));
      setSaving(false);
    }
  };

  return (
    <CapaDeModal>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 relative">

        {/* Close button */}
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors disabled:opacity-50"
        >
          <X className="w-4 h-4 text-gray-600" />
        </button>

        <div className="px-8 pt-8 pb-7 flex flex-col items-center text-center">
          {/* Title */}
          <h3 className="text-xl font-bold text-gray-900 mb-5 pr-4">
            ¿Estas seguro de dar de baja{" "}
            <em className="not-italic font-bold">{warehouseName}</em>?
          </h3>

          {/* Icon */}
          <img src="/icon-delete.svg" alt="Dar de baja" className="w-20 h-20 mb-5" />

          {/* Body */}
          <p className="text-sm text-gray-600 leading-relaxed mb-5">
            Este <em>{warehouseName}</em> tiene{" "}
            <strong>{toolCount}</strong> herramienta{toolCount !== 1 ? "s" : ""}{" "}
            asignada{toolCount !== 1 ? "s" : ""}. Si lo das de baja, deberás
            reasignarlas a otro almacén activo antes de continuar.
          </p>

          {/* Select almacén destino */}
          <div className="w-full mb-6">
            <SelectBuscable
              variante="formulario"
              opciones={otherWarehouses.map((w) => ({ value: String(w.id), label: w.name }))}
              value={targetId}
              onChange={setTargetId}
              placeholder="Seleccionar almacén de traspaso..."
              placeholderDeBusqueda="Buscar almacén..."
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 w-full">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className={estiloDeBoton("secundario", "flex-1")}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!targetId || saving}
              className={estiloDeBoton("peligro", "flex-1")}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Reasignando...
                </>
              ) : (
                "Dar de baja y reasignar"
              )}
            </button>
          </div>
        </div>
      </div>
    </CapaDeModal>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

type View = "list" | "detail" | "edit" | "create";

const AlmacenesListView = ({ onBack }: { onBack: () => void }) => {
  const [view, setView]                           = useState<View>("list");
  const [selectedAlmacen, setSelectedAlmacen]     = useState<Almacen | null>(null);
  const [selectedRaw, setSelectedRaw]             = useState<WarehouseApiResponse | null>(null);

  // Data
  const [almacenes, setAlmacenes]   = useState<Almacen[]>([]);
  const [rawList, setRawList]       = useState<WarehouseApiResponse[]>([]);
  const [usersMap, setUsersMap]     = useState<Record<string, string>>({});
  const [loading, setLoading]       = useState(true);

  // Reasign modal
  const [confirmBaja, setConfirmBaja] = useState<{ id: string; nombre: string } | null>(null);

  const [reasignModal, setReasignModal] = useState<{
    warehouseId: string;
    warehouseName: string;
    toolCount: number;
    tools: ToolSummary[];
  } | null>(null);

  // Filters
  const [search, setSearch]                       = useState("");
  const [filterResponsable, setFilterResponsable] = useState("");
  const [filterEstado, setFilterEstado]           = useState("");

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [warehouses, users] = await Promise.all([
        almacenApi.findAll(),
        apiService.request<UserProfile[]>("/users/public/all"),
      ]);
      const map = buildUsersMap(users);
      setUsersMap(map);
      setRawList(warehouses);
      setAlmacenes(warehouses.map((w) => mapWarehouse(w, map)));
    } catch (err: unknown) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Derived list ───────────────────────────────────────────────────────────

  const uniqueResponsables = useMemo(
    () => [...new Set(almacenes.filter((a) => a.responsable !== "Sin responsable").map((a) => a.responsable))],
    [almacenes],
  );

  const rows = useMemo(
    () =>
      almacenes.filter((a) => {
        const q = search.toLowerCase();
        const matchSearch =
          !q ||
          a.nombre.toLowerCase().includes(q) ||
          a.responsable.toLowerCase().includes(q) ||
          a.descripcion.toLowerCase().includes(q);
        return (
          matchSearch &&
          (!filterResponsable || a.responsable === filterResponsable) &&
          (!filterEstado      || a.estado      === filterEstado)
        );
      }),
    [almacenes, search, filterResponsable, filterEstado],
  );

  // ── CRUD handlers ──────────────────────────────────────────────────────────

  const handleCreate = async (data: AlmacenFormData) => {
    await almacenApi.create({
      name: data.nombre,
      description: data.descripcion || undefined,
      responsibleUserId: data.responsableUserId || undefined,
    });
    await fetchData();
  };

  const handleUpdate = async (data: AlmacenFormData) => {
    if (!selectedRaw) return;
    await almacenApi.update(selectedRaw.id, {
      name: data.nombre,
      description: data.descripcion || undefined,
      responsibleUserId: data.responsableUserId || undefined,
    });
    await fetchData();
    // Refresh selectedAlmacen display after edit
    const refreshed = await almacenApi.findOne(selectedRaw.id);
    setSelectedAlmacen(mapWarehouse(refreshed, usersMap));
    setSelectedRaw(refreshed);
  };

  const handleDarDeBaja = async (id: string) => {
    // Fetch tools in this warehouse
    interface PaginatedTools {
      data: ToolSummary[];
      total: number;
    }
    const res = await apiService.request<PaginatedTools>(
      `/almacen/herramientas?warehouseId=${id}&limit=100`,
    );
    const tools  = Array.isArray(res?.data) ? res.data : [];
    const total  = res?.total ?? tools.length;

    if (total === 0) {
      // No tools → ask for confirmation before deactivating
      const raw = rawList.find((w) => w.id === id);
      setConfirmBaja({ id, nombre: raw?.name ?? id });
    } else {
      // Show reassignment modal
      const raw = rawList.find((w) => w.id === id);
      setReasignModal({
        warehouseId:   id,
        warehouseName: raw?.name ?? id,
        toolCount:     total,
        tools,
      });
    }
  };

  const handleReasignConfirm = async (targetWarehouseId: string) => {
    if (!reasignModal) return;
    const { warehouseId, tools } = reasignModal;

    // Reasign all tools to the target warehouse in parallel
    await Promise.all(
      tools.map((t) =>
        apiService.request(`/almacen/herramientas/${t.id}`, {
          method: "PATCH",
          body: JSON.stringify({ warehouseId: targetWarehouseId }),
        }),
      ),
    );

    // Now deactivate the warehouse
    await almacenApi.remove(warehouseId);
    setReasignModal(null);
    await fetchData();
    setView("list");
  };

  const handleConfirmBaja = async () => {
    if (!confirmBaja) return;
    await almacenApi.remove(confirmBaja.id);
    setConfirmBaja(null);
    await fetchData();
    setView("list");
  };

  // ── Confirm baja modal (shared fragment) ───────────────────────────────────
  const ConfirmBajaModal = confirmBaja ? (
    <CapaDeModal>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 relative">

        {/* Close button */}
        <button
          type="button"
          onClick={() => setConfirmBaja(null)}
          className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
        >
          <X className="w-4 h-4 text-gray-600" />
        </button>

        <div className="px-8 pt-8 pb-7 flex flex-col items-center text-center">
          {/* Title */}
          <h3 className="text-xl font-bold text-gray-900 mb-5 pr-4">
            ¿Estas seguro de dar de baja{" "}
            <em className="not-italic font-bold">{confirmBaja.nombre}</em>?
          </h3>

          {/* Icon */}
          <img src="/icon-delete.svg" alt="Dar de baja" className="w-20 h-20 mb-5" />

          {/* Body */}
          <p className="text-sm text-gray-600 leading-relaxed mb-6">
            Este <em>{confirmBaja.nombre}</em> no tiene herramientas asignadas.
            Si lo das de baja, esta acción no se puede deshacer.
          </p>

          {/* Buttons */}
          <div className="flex gap-3 w-full">
            <button
              type="button"
              onClick={() => setConfirmBaja(null)}
              className={estiloDeBoton("secundario", "flex-1")}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmBaja}
              className={estiloDeBoton("peligro", "flex-1")}
            >
              Dar de baja
            </button>
          </div>
        </div>
      </div>
    </CapaDeModal>
  ) : null;

  // ── Sub-views ──────────────────────────────────────────────────────────────

  if (view === "detail" && selectedAlmacen) {
    return (
      <>
        {ConfirmBajaModal}
        {/* Reasign modal — rendered on top of detail view too */}
        {reasignModal && (
          <ReasignHerramientasModal
            warehouseName={reasignModal.warehouseName}
            toolCount={reasignModal.toolCount}
            otherWarehouses={rawList.filter(
              (w) => w.id !== reasignModal.warehouseId && w.isActive,
            )}
            onConfirm={handleReasignConfirm}
            onCancel={() => setReasignModal(null)}
          />
        )}
        <AlmacenDetailView
          almacen={selectedAlmacen}
          onBack={() => setView("list")}
          onEdit={() => setView("edit")}
          onDarDeBaja={handleDarDeBaja}
        />
      </>
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────

  return (
    <div>

      {/* Confirm baja modal */}
      {ConfirmBajaModal}

      {/* Reasign modal */}
      {reasignModal && (
        <ReasignHerramientasModal
          warehouseName={reasignModal.warehouseName}
          toolCount={reasignModal.toolCount}
          otherWarehouses={rawList.filter(
            (w) => w.id !== reasignModal.warehouseId && w.isActive,
          )}
          onConfirm={handleReasignConfirm}
          onCancel={() => setReasignModal(null)}
        />
      )}

      {/* Create modal */}
      {view === "create" && (
        <AlmacenFormModal
          mode="create"
          onClose={() => setView("list")}
          onSubmit={handleCreate}
        />
      )}

      {/* Edit modal */}
      {view === "edit" && selectedRaw && (
        <AlmacenFormModal
          mode="edit"
          initial={{
            nombre:            selectedRaw.name ?? "",
            responsableUserId: selectedRaw.responsibleUserId ?? "",
            descripcion:       selectedRaw.description ?? "",
          }}
          onClose={() => setView("detail")}
          onSubmit={handleUpdate}
        />
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onBack}
            className={estiloDeBoton("secundario")}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div>
            <TituloDePantalla nivel="interna">Catálogo de almacén</TituloDePantalla>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setView("create")}
          className={estiloDeBoton("primario")}
        >
          <CirclePlus className="w-4 h-4" />
          Agregar almacén
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap justify-start items-center gap-3 mb-6">
        <div className="relative w-[280px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar en almacenes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
          />
        </div>
        <FilterSelect
          label="Responsable"
          options={(uniqueResponsables).map((o) => ({ value: o, label: o }))}
          value={filterResponsable}
          onChange={setFilterResponsable}
        ancho="contenido"
          />
        <FilterSelect
          label="Estado"
          options={(["Activo", "Inactivo"]).map((o) => ({ value: o, label: o }))}
          value={filterEstado}
          onChange={setFilterEstado}
        ancho="contenido"
          />
        <button
          onClick={() => { setSearch(""); setFilterResponsable(""); setFilterEstado(""); }}
          className="text-sm font-semibold text-[#315FB9] hover:underline"
        >
          Limpiar
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Cargando almacenes…</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr style={{ backgroundColor: "#FAF9FF", borderBottom: "1px solid #E4E4E4" }}>
                  {["Nombre", "Responsable", "Descripción", "Estado"].map((h) => (
                    <th
                      key={h}
                      className="text-left py-3 px-4 text-sm font-semibold first:pl-6 last:pr-6 whitespace-nowrap"
                      style={{ color: "#534D59" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      <div className="flex flex-col items-center justify-center py-16 gap-2">
                        <p className="text-sm font-medium text-gray-500">Sin resultados</p>
                        <p className="text-sm text-gray-400">Intenta con otros filtros.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const raw = rawList.find((r) => r.id === row.id);
                    return (
                      <tr
                        key={row.id}
                        onClick={() => {
                          setSelectedAlmacen(row);
                          setSelectedRaw(raw ?? null);
                          setView("detail");
                        }}
                        className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                        style={{ borderBottom: "1px solid #E4E4E4" }}
                      >
                        <td className="py-4 px-4 pl-6 text-gray-700 font-medium whitespace-nowrap">{row.nombre}</td>
                        <td className="py-4 px-4 text-gray-600 whitespace-nowrap">{row.responsable}</td>
                        <td className="py-4 px-4 text-gray-500 max-w-sm truncate">{row.descripcion || <span className="italic text-gray-300">Sin descripción</span>}</td>
                        <td className="py-4 px-4 pr-6 whitespace-nowrap">
                          <Badge color={row.estado === "Activo" ? "verde" : "gris"}>
                            {row.estado}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlmacenesListView;
